import {
  escapeHtml,
  minutesFromMidnight,
  isoFromMinutesOnDate,
  parseLocalDate,
  formatDateString,
  statusCssVariable,
} from "../utils";

// WeekView renders a 7-day grid: time column on the left, one column per day
// of the visible week (Mon..Sun), 30-minute rows. Events are absolute-positioned
// inside per-day layers based on their `start` date.
//
// Same dumb-component contract as DayView: it only renders and dispatches
// synthetic events through the CalendarScheduler instance.
export class WeekView {
  constructor(scheduler) {
    this.scheduler = scheduler;
    this.element = null;
    this.dayLayers = new Map(); // YYYY-MM-DD -> HTMLElement
    this._domListeners = [];
    this._selectedDayIdx = null; // mobile-only: which day in the week is visible
  }

  mount(parent) {
    this.element = document.createElement("div");
    this.element.className =
      "calendar-week-view bg-white rounded-lg border border-gray-200 overflow-x-auto";
    parent.appendChild(this.element);
    this._render();
  }

  setData() {
    if (!this.element) return;
    this._render();
  }

  destroy() {
    this._domListeners.forEach(({ target, event, handler }) =>
      target.removeEventListener(event, handler)
    );
    this._domListeners = [];
    this.dayLayers.clear();
    if (this.element?.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }

  // ---- internal ----

  _render() {
    this._tearDownDomListeners();
    this.dayLayers.clear();
    this.element.replaceChildren();

    const days = this._daysInWeek();

    if (this.scheduler.compact) {
      this._renderMobile(days);
    } else {
      this._renderDesktop(days);
    }
  }

  _renderDesktop(days) {
    const gridTemplateColumns = `var(--calendar-time-column-width) repeat(7, minmax(var(--calendar-min-resource-width), 1fr))`;

    this.element.appendChild(this._buildHeader(days, gridTemplateColumns));
    this.element.appendChild(this._buildBody(days, gridTemplateColumns));
    this._paintEvents(days);
  }

  // Mobile: render only the selected day's column with a picker (prev/day/next)
  // above. Touch swipe cycles through days within the week.
  _renderMobile(days) {
    // Default to today if it's in the visible week, otherwise day 0 (Monday).
    if (this._selectedDayIdx === null) {
      const todayStr = formatDateString(new Date());
      const todayIdx = days.findIndex((d) => formatDateString(d) === todayStr);
      this._selectedDayIdx = todayIdx >= 0 ? todayIdx : 0;
    }
    if (this._selectedDayIdx >= days.length) this._selectedDayIdx = 0;

    const day = days[this._selectedDayIdx];

    this.element.appendChild(this._buildMobilePicker(days, day));
    this.element.appendChild(this._buildMobileBody(day));
    this._paintEvents([day]);
    this._enableSwipe(days);
  }

  _buildMobilePicker(days, day) {
    const picker = document.createElement("div");
    picker.className =
      "flex items-center justify-between border-b border-gray-200 p-3 bg-white sticky top-0";

    const prev = document.createElement("button");
    prev.type = "button";
    prev.className =
      "px-3 py-2 text-sm font-medium text-gray-600 hover:text-black";
    prev.style.minHeight = "44px";
    prev.style.minWidth = "44px";
    prev.textContent = "‹";
    prev.setAttribute("aria-label", "Previous day");
    const onPrev = () => this._cycleDay(-1, days.length);
    prev.addEventListener("click", onPrev);
    this._domListeners.push({ target: prev, event: "click", handler: onPrev });

    const isToday = formatDateString(day) === formatDateString(new Date());
    const label = document.createElement("div");
    label.className = "flex-1 text-center";
    label.innerHTML = `
      <div class="text-xs ${isToday ? "text-black" : "text-gray-500"}">${escapeHtml(day.toLocaleDateString("en-US", { weekday: "long" }))}</div>
      <div class="text-sm font-bold text-black">${escapeHtml(day.toLocaleDateString("en-US", { month: "short", day: "numeric" }))}</div>
    `;

    const next = document.createElement("button");
    next.type = "button";
    next.className =
      "px-3 py-2 text-sm font-medium text-gray-600 hover:text-black";
    next.style.minHeight = "44px";
    next.style.minWidth = "44px";
    next.textContent = "›";
    next.setAttribute("aria-label", "Next day");
    const onNext = () => this._cycleDay(+1, days.length);
    next.addEventListener("click", onNext);
    this._domListeners.push({ target: next, event: "click", handler: onNext });

    picker.append(prev, label, next);
    return picker;
  }

  _buildMobileBody(day) {
    const gridTemplateColumns = `var(--calendar-time-column-width) 1fr`;
    const body = document.createElement("div");
    body.className = "relative grid";
    body.style.gridTemplateColumns = gridTemplateColumns;
    body.dataset.role = "grid";

    const slots = this._timeSlots();

    slots.forEach((slot, rowIdx) =>
      body.appendChild(this._buildTimeLabel(slot, rowIdx))
    );

    slots.forEach((slot, rowIdx) =>
      body.appendChild(this._buildSlotCell(day, slot, 0, rowIdx))
    );
    const layer = this._buildEventLayer(day, 0, slots.length);
    this.dayLayers.set(formatDateString(day), layer);
    body.appendChild(layer);

    return body;
  }

  _cycleDay(direction, total) {
    if (this._selectedDayIdx === null) return;
    this._selectedDayIdx =
      (this._selectedDayIdx + direction + total) % total;
    this._render();
  }

  _enableSwipe(days) {
    let touchStartX = 0;
    let touchStartY = 0;
    const onStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      touchStartX = t.clientX;
      touchStartY = t.clientY;
    };
    const onEnd = (e) => {
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - touchStartX;
      const dy = t.clientY - touchStartY;
      if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      this._cycleDay(dx < 0 ? +1 : -1, days.length);
    };
    this.element.addEventListener("touchstart", onStart, { passive: true });
    this.element.addEventListener("touchend", onEnd, { passive: true });
    this._domListeners.push(
      { target: this.element, event: "touchstart", handler: onStart },
      { target: this.element, event: "touchend", handler: onEnd }
    );
  }

  _buildHeader(days, gridTemplateColumns) {
    const header = document.createElement("div");
    header.className = "grid border-b border-gray-200";
    header.style.gridTemplateColumns = gridTemplateColumns;
    header.style.backgroundColor = "var(--calendar-header-bg)";

    const timeHeader = document.createElement("div");
    timeHeader.className = "p-3 font-bold text-gray-500 border-r border-gray-200";
    timeHeader.style.fontSize = "var(--calendar-time-label-size)";
    timeHeader.textContent = "TIME";
    header.appendChild(timeHeader);

    days.forEach((day) => {
      const cell = document.createElement("div");
      cell.className = "p-3 border-r border-gray-200 text-center";
      cell.dataset.date = formatDateString(day);

      const isToday = formatDateString(day) === formatDateString(new Date());
      const dayName = day.toLocaleDateString("en-US", { weekday: "short" });
      const dayNumber = day.getDate();

      cell.innerHTML = `
        <div class="text-xs font-medium ${isToday ? "text-black" : "text-gray-500"}">${escapeHtml(dayName)}</div>
        <div class="text-lg font-bold ${isToday ? "text-black" : "text-gray-700"}">${dayNumber}</div>
      `;
      header.appendChild(cell);
    });

    return header;
  }

  _buildBody(days, gridTemplateColumns) {
    const body = document.createElement("div");
    body.className = "relative grid";
    body.style.gridTemplateColumns = gridTemplateColumns;
    body.dataset.role = "grid";

    const slots = this._timeSlots();

    // Time column labels (column 1) — explicit grid-row to keep auto-placement honest.
    slots.forEach((slot, rowIdx) =>
      body.appendChild(this._buildTimeLabel(slot, rowIdx))
    );

    // Per-day column: clickable empty slot cells + absolute event layer.
    days.forEach((day, colIdx) => {
      slots.forEach((slot, rowIdx) =>
        body.appendChild(this._buildSlotCell(day, slot, colIdx, rowIdx))
      );
      const layer = this._buildEventLayer(day, colIdx, slots.length);
      this.dayLayers.set(formatDateString(day), layer);
      body.appendChild(layer);
    });

    return body;
  }

  _buildTimeLabel(slot, rowIdx) {
    const cell = document.createElement("div");
    cell.className =
      "border-r border-b flex items-start justify-center pt-1 text-gray-500";
    cell.style.height = "var(--calendar-slot-height)";
    cell.style.borderColor = "var(--calendar-grid-line)";
    cell.style.fontSize = "var(--calendar-time-label-size)";
    cell.style.gridColumn = "1";
    cell.style.gridRow = String(rowIdx + 1);
    cell.textContent = slot.label;
    return cell;
  }

  _buildSlotCell(day, slot, colIdx, rowIdx) {
    const dayString = formatDateString(day);
    const cell = document.createElement("div");
    cell.className =
      "calendar-slot border-r border-b cursor-pointer transition-colors";
    cell.style.height = "var(--calendar-slot-height)";
    cell.style.borderColor = "var(--calendar-grid-line)";
    cell.style.gridColumn = String(colIdx + 2);
    cell.style.gridRow = String(rowIdx + 1);
    cell.dataset.date = dayString;
    cell.dataset.startTime = isoFromMinutesOnDate(
      dayString,
      slot.hour * 60 + slot.minute
    );

    const enter = () => {
      cell.style.backgroundColor = "var(--calendar-empty-slot-hover)";
    };
    const leave = () => {
      cell.style.backgroundColor = "";
    };
    const click = (event) => this._handleSlotClick(event, day, slot);

    cell.addEventListener("mouseenter", enter);
    cell.addEventListener("mouseleave", leave);
    cell.addEventListener("click", click);
    this._domListeners.push(
      { target: cell, event: "mouseenter", handler: enter },
      { target: cell, event: "mouseleave", handler: leave },
      { target: cell, event: "click", handler: click }
    );

    return cell;
  }

  _buildEventLayer(day, colIdx, totalRows) {
    const layer = document.createElement("div");
    layer.className = "calendar-event-layer relative";
    layer.dataset.date = formatDateString(day);
    layer.style.gridColumn = String(colIdx + 2);
    layer.style.gridRow = `1 / ${totalRows + 1}`;
    layer.style.pointerEvents = "none";
    return layer;
  }

  _paintEvents(days) {
    this.dayLayers.forEach((layer) => layer.replaceChildren());

    const visibleDates = new Set(days.map((d) => formatDateString(d)));

    this.scheduler.events.forEach((item) => {
      const eventDate = String(item.start).split("T")[0];
      if (!visibleDates.has(eventDate)) return;

      const layer = this.dayLayers.get(eventDate);
      if (!layer) return;

      layer.appendChild(this._buildEventNode(item));
    });
  }

  _buildEventNode(item) {
    const { topPx, heightPx } = this._positionFor(item);
    const node = document.createElement("button");
    node.type = "button";
    node.className =
      "calendar-event absolute left-1 right-1 border-l-4 shadow-sm text-left px-2 py-0.5 overflow-hidden hover:shadow-md transition-shadow";
    node.style.pointerEvents = "auto";
    node.style.top = `${topPx}px`;
    node.style.height = `${heightPx}px`;
    node.style.fontSize = "var(--calendar-event-text-size)";
    node.style.borderRadius = "var(--calendar-event-radius)";
    node.style.backgroundColor =
      item.kind === "block"
        ? "var(--calendar-block-bg)"
        : "var(--calendar-event-bg)";
    node.style.borderLeftColor = `var(${statusCssVariable(item)})`;
    node.dataset.eventId = String(item.id);
    node.dataset.kind = item.kind;
    node.dataset.status = item.status || "";
    node.dataset.locked = item.kind === "block" ? "true" : "false";
    node.innerHTML = item.bodyHtml || "";

    const click = (event) => {
      event.stopPropagation();
      this._handleEventClick(event, item);
    };
    node.addEventListener("click", click);
    this._domListeners.push({ target: node, event: "click", handler: click });

    if (item.kind !== "block") {
      this._enableDrag(node, item);
      node.style.cursor = "grab";
      this._appendResizeHandle(node, item);
    }

    return node;
  }

  // 6px bottom edge handle. Mousedown stops propagation so it doesn't trigger
  // the parent drag-to-move handler.
  _appendResizeHandle(node, item) {
    const handle = document.createElement("span");
    handle.className =
      "calendar-resize-handle absolute bottom-0 left-0 right-0";
    handle.style.height = "6px";
    handle.style.cursor = "ns-resize";
    handle.style.pointerEvents = "auto";
    handle.dataset.resizeHandle = "true";
    node.appendChild(handle);
    this._enableResize(handle, node, item);
  }

  _enableResize(handle, node, item) {
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const state = {
        startY: e.clientY,
        originalHeight: parseInt(node.style.height, 10) || 0,
        moved: false,
      };

      const onMove = (ev) => this._onResizeMove(ev, node, state);
      const onUp = (ev) =>
        this._onResizeEnd(ev, node, item, state, onMove, onUp);
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.body.style.userSelect = "none";
    };

    handle.addEventListener("mousedown", onMouseDown);
    this._domListeners.push({
      target: handle,
      event: "mousedown",
      handler: onMouseDown,
    });
  }

  _onResizeMove(e, node, state) {
    const dy = e.clientY - state.startY;

    if (!state.moved && Math.abs(dy) < 5) return;
    state.moved = true;

    const layout = this.scheduler.layout;
    const quarterPx = layout.slotHeightPx / 2;
    const snappedDy = Math.round(dy / quarterPx) * quarterPx;
    const newHeight = Math.max(quarterPx, state.originalHeight + snappedDy);
    node.style.height = `${newHeight}px`;
  }

  _onResizeEnd(e, node, item, state, onMove, onUp) {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.body.style.userSelect = "";

    if (!state.moved) return;

    const suppressClick = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      window.removeEventListener("click", suppressClick, true);
    };
    window.addEventListener("click", suppressClick, true);

    const layout = this.scheduler.layout;
    const newTop = parseInt(node.style.top, 10) || 0;
    const newHeight = parseInt(node.style.height, 10) || 0;
    const endMinutesOfDay =
      this.scheduler.startHour * 60 +
      ((newTop + newHeight) / layout.slotHeightPx) * layout.slotMinutes;

    // Resize doesn't change the date; reuse the date portion of item.start.
    const startDateStr = String(item.start).split("T")[0];
    const newEnd = isoFromMinutesOnDate(startDateStr, endMinutesOfDay);

    this.scheduler._emit("event-resize", {
      id: String(item.id),
      kind: item.kind,
      originalEnd: item.end,
      newEnd,
    });
  }

  // Mouse-only drag-to-move. Vertical drag changes the start time (snapped to
  // 15-min). Horizontal drag over a different day column re-parents the node
  // to that column's event layer; on drop, the event's date changes.
  // staffId is preserved (week view has no staff dimension).
  _enableDrag(node, item) {
    const onMouseDown = (e) => {
      if (e.button !== 0) return;
      if (e.target?.dataset?.resizeHandle === "true") return;
      e.preventDefault();

      const layer = node.parentElement;
      const state = {
        startX: e.clientX,
        startY: e.clientY,
        originalTop: parseInt(node.style.top, 10) || 0,
        originalLayer: layer,
        currentLayer: layer,
        moved: false,
      };

      const onMove = (ev) => this._onDragMove(ev, node, state);
      const onUp = (ev) => this._onDragEnd(ev, node, item, state, onMove, onUp);
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      document.body.style.userSelect = "none";
      node.style.cursor = "grabbing";
    };

    node.addEventListener("mousedown", onMouseDown);
    this._domListeners.push({
      target: node,
      event: "mousedown",
      handler: onMouseDown,
    });
  }

  _onDragMove(e, node, state) {
    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;

    if (!state.moved && Math.hypot(dx, dy) < 5) return;
    if (!state.moved) {
      state.moved = true;
      node.style.opacity = "0.7";
      node.style.zIndex = "10";
      node.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
      // Make the dragged node transparent to elementFromPoint so we can find
      // the column beneath the cursor.
      node.style.pointerEvents = "none";
    }

    const layout = this.scheduler.layout;
    const quarterPx = layout.slotHeightPx / 2;
    const snappedDeltaY = Math.round(dy / quarterPx) * quarterPx;
    const newTop = Math.max(0, state.originalTop + snappedDeltaY);
    node.style.top = `${newTop}px`;

    // Cross-day re-parenting: find any ancestor with [data-date] (slot cell,
    // header, or layer all carry it) and re-home the node into that day's
    // event layer.
    const target = document.elementFromPoint(e.clientX, e.clientY);
    const dateEl = target?.closest?.("[data-date]");
    const targetDate = dateEl?.dataset?.date;
    if (targetDate && targetDate !== state.currentLayer.dataset.date) {
      const targetLayer = this.dayLayers.get(targetDate);
      if (targetLayer) {
        targetLayer.appendChild(node);
        state.currentLayer = targetLayer;
      }
    }
  }

  _onDragEnd(e, node, item, state, onMove, onUp) {
    document.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseup", onUp);
    document.body.style.userSelect = "";
    node.style.cursor = "grab";

    if (!state.moved) return;

    node.style.opacity = "";
    node.style.zIndex = "";
    node.style.boxShadow = "";
    // `pointer-events` is inherited; restore "auto" explicitly (see DayView).
    node.style.pointerEvents = "auto";

    // Suppress the native click that fires after mouseup post-drag.
    const suppressClick = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
      window.removeEventListener("click", suppressClick, true);
    };
    window.addEventListener("click", suppressClick, true);

    const newTop = parseInt(node.style.top, 10) || 0;
    const layout = this.scheduler.layout;
    const startMinutesOfDay =
      this.scheduler.startHour * 60 +
      (newTop / layout.slotHeightPx) * layout.slotMinutes;
    const durationMinutes =
      minutesFromMidnight(item.end) - minutesFromMidnight(item.start);

    // Date comes from the layer the event is now living in.
    const targetDate = state.currentLayer.dataset.date;
    const newStart = isoFromMinutesOnDate(targetDate, startMinutesOfDay);
    const newEnd = isoFromMinutesOnDate(
      targetDate,
      startMinutesOfDay + durationMinutes
    );

    this.scheduler._emit("event-move", {
      id: String(item.id),
      kind: item.kind,
      originalStart: item.start,
      newStart,
      originalEnd: item.end,
      newEnd,
      // Week view has no staff dimension — preserve original staff.
      originalStaffId: String(item.staffId || ""),
      newStaffId: String(item.staffId || ""),
    });
  }

  _positionFor(item) {
    const layout = this.scheduler.layout;
    const dayStartMinutes = this.scheduler.startHour * 60;
    const startMinutes = minutesFromMidnight(item.start);
    const endMinutes = minutesFromMidnight(item.end);
    const topPx =
      ((startMinutes - dayStartMinutes) / layout.slotMinutes) *
      layout.slotHeightPx;
    const heightPx =
      ((endMinutes - startMinutes) / layout.slotMinutes) *
      layout.slotHeightPx;
    return { topPx: Math.max(0, topPx), heightPx: Math.max(8, heightPx) };
  }

  _timeSlots() {
    const slots = [];
    const startHour = this.scheduler.startHour;
    const endHour = this.scheduler.endHour;
    const minuteSteps = [0, 30];
    for (let hour = startHour; hour < endHour; hour++) {
      minuteSteps.forEach((minute) => {
        slots.push({
          hour,
          minute,
          label: `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`,
        });
      });
    }
    return slots;
  }

  // Days of the week containing this.scheduler.date, Monday-first.
  _daysInWeek() {
    const anchor = parseLocalDate(this.scheduler.date);
    const dayOfWeek = anchor.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(
      anchor.getFullYear(),
      anchor.getMonth(),
      anchor.getDate() - daysFromMonday
    );
    return Array.from({ length: 7 }, (_, i) => {
      return new Date(
        monday.getFullYear(),
        monday.getMonth(),
        monday.getDate() + i
      );
    });
  }

  _handleSlotClick(event, day, slot) {
    const dayString = formatDateString(day);
    const totalMinutes = slot.hour * 60 + slot.minute;
    this.scheduler._emit("slot-click", {
      staffId: null,
      startTime: isoFromMinutesOnDate(dayString, totalMinutes),
      endTime: isoFromMinutesOnDate(
        dayString,
        totalMinutes + this.scheduler.layout.slotMinutes
      ),
      clientX: event.clientX,
      clientY: event.clientY,
    });
  }

  _handleEventClick(event, item) {
    this.scheduler._emit("event-click", {
      kind: item.kind,
      id: String(item.id),
      appointmentId: item.kind === "appointment" ? String(item.id) : null,
      blockId: item.kind === "block" ? String(item.id) : null,
      clientX: event.clientX,
      clientY: event.clientY,
    });
  }

  _tearDownDomListeners() {
    this._domListeners.forEach(({ target, event, handler }) =>
      target.removeEventListener(event, handler)
    );
    this._domListeners = [];
  }
}
